/* eslint-disable @typescript-eslint/no-explicit-any */
import { useState } from 'react'
import { Lock, Mail, ShieldCheck, User, Wallet } from 'lucide-react'

import { Button, FormInput } from '@/components'
import { cn } from '@/utils/cn'

type SettingsTab = 'profile' | 'notifications' | 'privacy' | 'billing'

const TABS: {
  id: SettingsTab
  label: string
  icon: React.ComponentType<any>
}[] = [
  { id: 'profile', label: 'Profile', icon: User },
  { id: 'notifications', label: 'Notifications', icon: Mail },
  { id: 'privacy', label: 'Privacy & Security', icon: ShieldCheck },
  { id: 'billing', label: 'Billing', icon: Wallet },
]

function Toggle({
  checked,
  label,
}: {
  checked: boolean
  label: string
}): JSX.Element {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={checked}
      className={cn(
        'relative inline-flex h-5 w-9 items-center rounded-full border transition-colors',
        checked
          ? 'border-[#F28C28] bg-[#F28C28]'
          : 'border-gray-300 bg-gray-200'
      )}
    >
      <span
        className={cn(
          'inline-block h-4 w-4 transform rounded-full bg-white shadow transition-transform',
          checked ? 'translate-x-4' : 'translate-x-0.5'
        )}
        aria-hidden
      />
      <span className="sr-only">{label}</span>
    </button>
  )
}

export const ParentSettings: React.FC = () => {
  const [tab, setTab] = useState<SettingsTab>('profile')

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-semibold text-gray-900">Settings</h1>
        <p className="mt-1 text-sm text-gray-500">
          Manage your account preferences and settings.
        </p>
      </div>

      <div className="grid gap-6 lg:grid-cols-[260px,minmax(0,1fr)]">
        {/* Left side tab list */}
        <aside className="rounded-2xl border border-gray-100 bg-white p-3 space-y-1">
          {TABS.map(({ id, label, icon: Icon }) => {
            const isActive = id === tab
            return (
              <button
                key={id}
                type="button"
                onClick={() => setTab(id)}
                className={cn(
                  'flex w-full items-center gap-2 rounded-xl px-3 py-2 text-sm font-medium transition-colors',
                  isActive
                    ? 'bg-[#FFF7ED] text-[#EA580C]'
                    : 'text-gray-700 hover:bg-gray-50'
                )}
              >
                <Icon className="h-4 w-4" />
                {label}
              </button>
            )
          })}
        </aside>

        {/* Right side content */}
        <section className="rounded-3xl border border-gray-100 bg-white p-6 shadow-sm space-y-6">
          {tab === 'profile' && (
            <>
              <header className="space-y-1">
                <h2 className="text-sm font-semibold text-gray-900">
                  Profile information
                </h2>
                <p className="text-xs text-gray-500">
                  Update your personal information and contact details.
                </p>
              </header>

              <div className="grid gap-4 md:grid-cols-2">
                <FormInput label="First name" defaultValue="Sarah" />
                <FormInput label="Last name" defaultValue="Johnson" />
                <FormInput
                  label="Email address"
                  type="email"
                  defaultValue="sarah.johnson@example.com"
                />
                <FormInput
                  label="Phone number"
                  defaultValue="+1 (555) 123‑4567"
                />
                <FormInput label="Language" defaultValue="English (US)" />
                <FormInput label="Location" defaultValue="Houston, Texas" />
              </div>

              <div className="flex justify-end">
                <Button
                  type="button"
                  variant="primary"
                  className="rounded-xl bg-[#1D5AA6] px-5 py-2.5 text-sm font-semibold hover:bg-blue-800 focus:ring-blue-600"
                >
                  Save changes
                </Button>
              </div>
            </>
          )}

          {tab === 'notifications' && (
            <>
              <header className="space-y-1">
                <h2 className="text-sm font-semibold text-gray-900">
                  Notification preferences
                </h2>
                <p className="text-xs text-gray-500">
                  Choose how you want to receive updates and reminders.
                </p>
              </header>

              <div className="space-y-4">
                <div className="rounded-2xl border border-gray-100 bg-gray-50 px-4 py-3 flex items-center justify-between">
                  <div>
                    <p className="text-sm font-medium text-gray-900">
                      Email notifications
                    </p>
                    <p className="text-xs text-gray-500">
                      Receive class updates via email.
                    </p>
                  </div>
                  <Toggle checked label="Email notifications" />
                </div>

                <div className="rounded-2xl border border-gray-100 bg-gray-50 px-4 py-3 flex items-center justify-between">
                  <div>
                    <p className="text-sm font-medium text-gray-900">
                      WhatsApp notifications
                    </p>
                    <p className="text-xs text-gray-500">
                      Get quick updates via WhatsApp.
                    </p>
                  </div>
                  <Toggle checked={false} label="WhatsApp notifications" />
                </div>

                <div className="rounded-2xl border border-gray-100 bg-gray-50 px-4 py-3 flex items-center justify-between">
                  <div>
                    <p className="text-sm font-medium text-gray-900">
                      Class reminders
                    </p>
                    <p className="text-xs text-gray-500">
                      Reminders for upcoming classes.
                    </p>
                  </div>
                  <Toggle checked label="Class reminders" />
                </div>

                <div className="rounded-2xl border border-gray-100 bg-gray-50 px-4 py-3 flex items-center justify-between">
                  <div>
                    <p className="text-sm font-medium text-gray-900">
                      Payment reminders
                    </p>
                    <p className="text-xs text-gray-500">
                      Alerts for upcoming payments.
                    </p>
                  </div>
                  <Toggle checked label="Payment reminders" />
                </div>
              </div>
            </>
          )}

          {tab === 'privacy' && (
            <>
              <header className="space-y-1">
                <h2 className="text-sm font-semibold text-gray-900">
                  Privacy &amp; security
                </h2>
                <p className="text-xs text-gray-500">
                  Manage your privacy options and account security.
                </p>
              </header>

              <div className="space-y-4">
                <div className="rounded-2xl border border-gray-100 bg-gray-50 px-4 py-3 flex items-center justify-between">
                  <div>
                    <p className="text-sm font-medium text-gray-900">
                      Show progress
                    </p>
                    <p className="text-xs text-gray-500">
                      Display your children&apos;s progress on the leaderboard.
                    </p>
                  </div>
                  <Toggle checked label="Show progress" />
                </div>

                <div className="rounded-2xl border border-gray-100 bg-gray-50 px-4 py-3 space-y-3">
                  <p className="text-sm font-medium text-gray-900">Security</p>
                  <div className="grid gap-3 md:grid-cols-2">
                    <button
                      type="button"
                      className="flex items-center justify-between rounded-xl border border-gray-200 bg-white px-4 py-2.5 text-xs font-medium text-gray-700 hover:bg-gray-50"
                    >
                      <span className="flex items-center gap-2">
                        <Lock className="h-3.5 w-3.5" />
                        Change password
                      </span>
                    </button>
                    <button
                      type="button"
                      className="flex items-center justify-between rounded-xl border border-gray-200 bg-white px-4 py-2.5 text-xs font-medium text-gray-700 hover:bg-gray-50"
                    >
                      <span className="flex items-center gap-2">
                        <ShieldCheck className="h-3.5 w-3.5" />
                        Enable two‑factor authentication
                      </span>
                    </button>
                  </div>
                </div>
              </div>

              <div className="flex justify-end">
                <Button
                  type="button"
                  variant="primary"
                  className="rounded-xl bg-[#1D5AA6] px-5 py-2.5 text-sm font-semibold hover:bg-blue-800 focus:ring-blue-600"
                >
                  Save settings
                </Button>
              </div>
            </>
          )}

          {tab === 'billing' && (
            <>
              <header className="space-y-1">
                <h2 className="text-sm font-semibold text-gray-900">
                  Billing &amp; payments
                </h2>
                <p className="text-xs text-gray-500">
                  Manage your payment methods and billing information.
                </p>
              </header>

              <div className="rounded-2xl border border-gray-100 bg-gray-50 px-4 py-4 text-xs text-gray-700 space-y-1">
                <p className="font-medium text-gray-900">Billing address</p>
                <p>1234 Main Street</p>
                <p>Houston, TX 77001</p>
                <p>United States</p>
              </div>
            </>
          )}
        </section>
      </div>
    </div>
  )
}
